import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { PurchaseDialog, brl } from "../components/PurchaseDialog";
import { ProductCard } from "../components/ProductCard";
import { api } from "../lib/api";
import { Gift, Store, ShoppingBag, Plus } from "lucide-react";

const CategoryChips = ({ categories, active, setActive }) => (
  <div className="flex gap-2 overflow-x-auto pb-2 mb-8 -mx-1 px-1" data-testid="category-filters">
    {["Todas", ...categories].map((c) => (
      <button key={c} type="button" onClick={() => setActive(c)}
              className={`shrink-0 px-4 py-1.5 rounded-full text-sm border transition-colors ${active === c ? "gold-btn border-transparent" : "border-border text-muted-foreground hover:text-foreground"}`}
              data-testid={`category-filter-${c}`}>{c}</button>
    ))}
  </div>
);

const BalanceBar = ({ data, balances, navigate }) => (
  <Card className="p-4 mb-6 flex flex-wrap items-center justify-between gap-3 border-emerald-500/40 bg-emerald-500/5" data-testid="store-balance-bar">
    <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-sm">
      <span className="flex items-center gap-2"><ShoppingBag className="h-4 w-4 text-emerald-500" /> Saldo disponível:
        <b className="font-mono" data-testid="store-balance">{brl(balances.balance)}</b></span>
      {!data.is_buyer && <span>Saldo Consumo: <b className="font-mono" data-testid="store-consumption">{brl(balances.consumption)}</b></span>}
    </div>
    <div className="flex gap-2">
      {data.can_register_partner && (
        <Button size="sm" variant="outline" onClick={() => navigate("/minhas-empresas")} data-testid="store-my-partners-btn">
          <Plus className="h-3 w-3 mr-1" /> Cadastrar minha empresa
        </Button>
      )}
      <Button size="sm" variant="outline" onClick={() => navigate("/pedidos")} data-testid="store-orders-btn">Meus pedidos</Button>
    </div>
  </Card>
);

export default function Benefits() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [balances, setBalances] = useState({ balance: 0, consumption: 0 });
  const [buying, setBuying] = useState(null);
  const [cat, setCat] = useState("Todas");

  const loadBalances = useCallback((d) => {
    if (!d?.can_use) return;
    api.get("/wallet").then((r) => setBalances((b) => ({ ...b, balance: r.data.balance }))).catch(() => {});
    if (!d.is_buyer) api.get("/consumption").then((r) => setBalances((b) => ({ ...b, consumption: r.data.balance }))).catch(() => {});
  }, []);
  useEffect(() => {
    api.get("/benefits").then((r) => { setData(r.data); loadBalances(r.data); })
      .catch(() => setData({ items: [], categories: [], can_use: false }));
  }, [loadBalances]);

  const items = useMemo(() => (data?.items || []).filter((b) => cat === "Todas" || b.category === cat), [data, cat]);
  const onLocked = () => navigate(data?.logged_in ? "/dashboard" : "/cadastro");

  return (
    <div className="min-h-screen">
      <Header />
      <div className="pt-28 pb-20 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-10">
          <span className="section-eyebrow">Clube de vantagens</span>
          <h1 className="font-display text-3xl sm:text-5xl font-bold mt-4">
            Benefícios e <span className="gold-text">Empresas Parceiras</span>
          </h1>
          <p className="mt-4 text-sm sm:text-base text-muted-foreground max-w-2xl">
            Produtos e serviços com preço especial para associados Conecta Mais. Pague com seu saldo ou via Pix.
          </p>
        </div>
        {data?.can_use && <BalanceBar data={data} balances={balances} navigate={navigate} />}
        {data?.categories?.length > 0 && <CategoryChips categories={data.categories} active={cat} setActive={setCat} />}
        {!data ? <p className="text-sm text-muted-foreground">Carregando...</p> : data.items.length === 0 ? (
          <Card className="p-10 max-w-xl mx-auto text-center gold-border" data-testid="benefits-coming-soon">
            <Gift className="h-10 w-10 text-amber-400 mx-auto" />
            <div className="mt-4 font-display text-2xl font-bold">Em breve</div>
            <p className="mt-2 text-sm text-muted-foreground">Os parceiros e descontos do clube serão divulgados aqui.</p>
          </Card>
        ) : items.length === 0 ? (
          <p className="text-sm text-muted-foreground flex items-center gap-2" data-testid="benefits-empty-category"><Store className="h-4 w-4" /> Nenhum item nesta categoria.</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="benefits-grid">
            {items.map((b, i) => (
              <ProductCard key={b.id} b={b} index={i} canUse={data.can_use} onLocked={onLocked}
                           onPay={(mode) => setBuying({ item: b, mode })} />
            ))}
          </div>
        )}
        <PurchaseDialog item={buying?.item} mode={buying?.mode} balances={balances} buyer={!!data?.is_buyer}
                        onClose={() => setBuying(null)} onDone={() => loadBalances(data)} />
      </div>
    </div>
  );
}
